import { useCallback, useEffect, useRef } from 'react'

import { useDebounceAsync } from 'commons/hooks/common'
import { Request } from 'commons/utils/request'
import uniqBy from 'lodash/uniqBy'

import { ChoiceOption } from '../../../types'
import { SelectContext, UseFetchOptions } from '../types'

export const useFetch = (context: SelectContext, options: UseFetchOptions) => {
  const { serverOptions } = options
  const { searchText, initialData, setSearchedData, setIsSearching, selectedValues } = context

  const firstLoadSearchText = useRef(searchText)
  const isFirstLoad = useRef(true)

  const fetch = useCallback(
    async (customFilters?: any): Promise<ChoiceOption[]> => {
      if (serverOptions) {
        const requestOptions = serverOptions?.requestSearchString
          ? {
              [serverOptions?.requestSearchString]: searchText
            }
          : {
              _options: {
                offset: 0,
                limit: serverOptions.limit,
                filters: customFilters
                  ? customFilters
                  : searchText && serverOptions.searchFields?.length
                    ? ([
                        {
                          type: 'or',
                          filters: serverOptions.searchFields.map((field) => ({
                            field,
                            type: 'like',
                            value: `%${searchText}%`
                          }))
                        }
                      ]
                        .concat(serverOptions.filters as any)
                        .filter(Boolean) as any)
                    : serverOptions.filters
              }
            }
        const data = await Request.post(serverOptions.url, requestOptions)

        // TODO: refactor it. We have to do this tricky fix, because different API endpoint returns
        // TODO: data in different formats.
        // eslint-disable-next-line
        const collection = Array.isArray(data?.records) ? data.records : Array.isArray(data) ? data : []
        const processedCollection = collection.map(serverOptions.mapper).filter(Boolean)
        if (processedCollection) {
          return Promise.resolve(processedCollection)
        }
      }
      return Promise.resolve([])
    },
    [serverOptions, searchText]
  )

  const debouncedFetch = useDebounceAsync(() => fetch(), 600)

  const initialFetch = async () => {
    const selectedData = await fetch([
      {
        type: 'in',
        field: 'id',
        value: selectedValues
      }
    ])
    const unselectedData = await fetch()
    return Promise.resolve(uniqBy([...(selectedData || []), ...(unselectedData || [])], 'value'))
  }

  useEffect(() => {
    if (!serverOptions) {
      return
    }

    if (firstLoadSearchText.current === searchText && initialData.length !== 0) {
      return
    }

    if (isFirstLoad.current) {
      isFirstLoad.current = false

      if (!serverOptions?.requestSearchString) {
        setIsSearching(true)
        initialFetch()
          .then((data) => setSearchedData(data))
          .finally(() => setIsSearching(false))
      }
    } else {
      setIsSearching(true)
      debouncedFetch()
        .then((data) => setSearchedData(data))
        .finally(() => setIsSearching(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])
}
